import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../../contexts/SessionContext';
import API_URL from '../../../constants/environment';
import YaxleLogo from '../../../assets/yaxleLogoBlue.png';
import { OrderedTab, SelectionTab } from '../../../hooks/useGetOrderDetail';
import { generateSupplierOrderSummaryPdf, SupplierOrderSummaryInput } from '../../../utils/supplierOrderSummaryPdf';

export interface SupplierOrderChoice {
    id: string;
    title: string;
    displayId: string | null;
    status: string;
    partCount: number;
}

export interface OrderSummaryPdfFile {
    filename: string;
    pdfBase64: string;
}

export function supplierOrdersOnOrder(tabs: Array<OrderedTab> | undefined): SupplierOrderChoice[] {
    const all = tabs?.find((tab) => tab.key === 'all');
    const sections = all ? all.sections : (tabs ?? []).flatMap((tab) => tab.sections);
    const seen = new Set<string>();
    const choices: SupplierOrderChoice[] = [];
    for (const section of sections) {
        if (seen.has(section.key)) continue;
        seen.add(section.key);
        choices.push({
            id: section.key,
            title: section.title,
            displayId: section.displayId,
            status: section.status,
            partCount: section.parts.length,
        });
    }
    return choices;
}

function bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
}

function fileSlug(value: string): string {
    const clean = value.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
    return clean || 'order';
}

function adjustmentsByPart(selection: Array<SelectionTab> | undefined) {
    const map = new Map<string, { quotedListPrice: number; adjustedListPrice: number }>();
    const tabs = selection?.some((tab) => tab.key === 'all')
        ? selection.filter((tab) => tab.key === 'all')
        : (selection ?? []);
    for (const tab of tabs) {
        for (const part of tab.parts) {
            if (map.has(part.partId)) continue;
            if (!part.listPriceAdjusted || part.listPrice == null || part.originalListPrice == null) continue;
            if (part.listPrice === part.originalListPrice) continue;
            map.set(part.partId, { quotedListPrice: part.originalListPrice, adjustedListPrice: part.listPrice });
        }
    }
    return map;
}

function sectionsToPdfInput(
    tabs: Array<OrderedTab> | undefined,
    ids: string[] | 'all',
    header: Omit<SupplierOrderSummaryInput, 'orders' | 'generatedAt'>,
    selection: Array<SelectionTab> | undefined,
): SupplierOrderSummaryInput {
    const adjustments = adjustmentsByPart(selection);
    const all = tabs?.find((tab) => tab.key === 'all');
    const sections = (all ? all.sections : (tabs ?? []).flatMap((tab) => tab.sections))
        .filter((section, index, list) => list.findIndex((item) => item.key === section.key) === index)
        .filter((section) => ids === 'all' || ids.includes(section.key));
    const occurrences = new Map<string, number>();
    for (const section of sections) {
        for (const part of section.parts) {
            occurrences.set(part.partId, (occurrences.get(part.partId) ?? 0) + 1);
        }
    }
    return {
        ...header,
        generatedAt: new Date(),
        orders: sections.map((section) => ({
            supplierName: section.title,
            reference: section.displayId,
            status: section.status.charAt(0).toUpperCase() + section.status.slice(1),
            source: ({
                partstrader: 'PartsTrader',
                opstrax: 'OpsTrax',
                ccc: 'CCC',
                'ccc-sp': 'CCC',
                tesla: 'Tesla',
                collisionlink: 'CollisionLink',
                inventory: 'Inventory',
                mitchell: 'Mitchell',
            } as Record<string, string>)[section.source ?? ''] ?? (section.source ?? ''),
            placedAt: section.timePlaced ? new Date(section.timePlaced) : null,
            parts: section.parts.map((part) => {
                const listPrice = part.correctedListPrice ?? part.listPrice;
                const adjustment = occurrences.get(part.partId) === 1 ? adjustments.get(part.partId) : undefined;
                // Match on the uncorrected ordered price, not the supplier-corrected
                // one — a correction can differ from both the quote and the
                // adjustment, and the PDF shows the corrected list alongside them.
                const adjustmentMatchesLine = !!adjustment && (
                    part.listPrice === adjustment.adjustedListPrice || part.listPrice === adjustment.quotedListPrice
                );
                return {
                    name: part.name,
                    partNumber: part.correctedPartNumber || part.partNumber,
                    supplierPartNumber: part.supplierPartNumber ?? null,
                    partType: part.type,
                    qty: part.quantity,
                    listPrice,
                    quotedListPrice: adjustmentMatchesLine ? adjustment?.quotedListPrice ?? null : null,
                    adjustedListPrice: adjustmentMatchesLine ? adjustment?.adjustedListPrice ?? null : null,
                    buyPrice: part.correctedBuyPrice ?? part.buyPrice,
                    delivery: part.correctedDelivery || part.delivery,
                    disposition: part.returned
                        ? 'Returned'
                        : part.cancelled || section.status === 'cancelled'
                            ? 'Cancelled'
                            : section.status === 'rejected'
                                ? 'Rejected'
                                : null,
                };
            }),
        })),
    };
}

async function loadLogo(): Promise<Uint8Array | null> {
    try {
        const res = await fetch(YaxleLogo);
        if (!res.ok) return null;
        return new Uint8Array(await res.arrayBuffer());
    } catch {
        return null;
    }
}

export function exportErrorMessage(error: unknown): string {
    if (axios.isAxiosError(error)) {
        const message = (error.response?.data as { message?: string } | undefined)?.message;
        if (message?.trim()) return message.trim();
    }
    return 'Could not export this purchase order summary.';
}

export function saveAndPrintPdf(file: OrderSummaryPdfFile, preview: Window | null): boolean {
    const binary = atob(file.pdfBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = file.filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    const canPrint = !!preview && !preview.closed;
    if (canPrint && preview) {
        const printPreview = () => {
            try {
                preview.focus();
                preview.print();
            } catch {
                // The file is already saved.
            }
        };
        preview.addEventListener('load', printPreview, { once: true });
        preview.location.href = url;
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
    return canPrint;
}

export function useExportSupplierOrderSummary() {
    const sessionContext = useContext(SessionContext);

    const exportSummary = async (
        orderId: string,
        supplierOrderIds: string[] | 'all',
        tabs: Array<OrderedTab> | undefined,
        header: Omit<SupplierOrderSummaryInput, 'orders' | 'generatedAt'>,
        selection?: Array<SelectionTab>,
    ): Promise<OrderSummaryPdfFile> => {
        const sessionId = sessionContext?.isAuthenticated ? sessionContext.sessionId : '';
        try {
            const response = await axios.post<OrderSummaryPdfFile>(
                `${API_URL}/order/summary-pdf`,
                {
                    orderId,
                    ...(supplierOrderIds === 'all' ? {} : { supplierOrderIds }),
                },
                { headers: { Authorization: sessionId }, timeout: 8000 },
            );
            if (response.data?.pdfBase64 && response.data.filename) return response.data;
        } catch (error) {
            const status = axios.isAxiosError(error) ? error.response?.status : undefined;
            const routeMissing = axios.isAxiosError(error) && (status === 404 || status == null);
            if (!routeMissing) throw error;
        }

        const preparedBy = sessionContext?.isAuthenticated
            ? `${sessionContext.firstName} ${sessionContext.lastName}`.trim()
            : null;
        const logo = await loadLogo();
        const bytes = await generateSupplierOrderSummaryPdf(
            sectionsToPdfInput(tabs, supplierOrderIds, { ...header, preparedBy: header.preparedBy ?? preparedBy }, selection),
            logo,
        );
        return {
            filename: `RO-${fileSlug(header.roNumber)}-order-summary.pdf`,
            pdfBase64: bytesToBase64(bytes),
        };
    };

    return { exportSummary };
}
